import { useState } from 'react'
import { ImageIcon, ShieldCheck } from 'lucide-react'
import {
  createInboundEmailDocument,
  prepareInboundEmailForRendering,
  type InboundAttachmentRef,
} from '@/lib/email-rendering'

interface MessageBodyProps {
  html?: string | null
  text?: string | null
  preview?: string | null
  attachments?: InboundAttachmentRef[]
}

// CHECKPOINT: PRD-10 FR-10.14.5 — Read inbound message content without exposing dashboard state to sender HTML.
export function MessageBody({ html, text, preview, attachments }: MessageBodyProps) {
  // Store approval against the exact HTML value so moving to another message
  // never briefly reuses a previous message's remote-image permission.
  const [approvedHtmlForRemoteImages, setApprovedHtmlForRemoteImages] = useState<string | null>(null)
  const loadRemoteImages = approvedHtmlForRemoteImages === html
  const renderedEmail = html
    ? prepareInboundEmailForRendering(html, attachments, loadRemoteImages)
    : null
  const emailDocument = renderedEmail ? createInboundEmailDocument(renderedEmail.html) : null

  if (emailDocument && renderedEmail) {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/80 bg-card/75 px-4 py-3 text-sm">
          <div className="flex items-start gap-2 text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>
              {renderedEmail.blockedRemoteImageCount > 0 && !loadRemoteImages
                ? `${renderedEmail.blockedRemoteImageCount} remote ${renderedEmail.blockedRemoteImageCount === 1 ? 'image is' : 'images are'} hidden to protect your privacy.`
                : 'This message is isolated from your dashboard session.'}
            </span>
          </div>
          {renderedEmail.blockedRemoteImageCount > 0 && !loadRemoteImages && (
            <button
              type="button"
              onClick={() => setApprovedHtmlForRemoteImages(html ?? null)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium transition-colors hover:bg-accent"
            >
              <ImageIcon className="h-3.5 w-3.5" />
              Load images
            </button>
          )}
          {loadRemoteImages && (
            <button
              type="button"
              onClick={() => setApprovedHtmlForRemoteImages(null)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium transition-colors hover:bg-accent"
            >
              Hide remote images
            </button>
          )}
        </div>
        <iframe
          title="Email content"
          srcDoc={emailDocument}
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="no-referrer"
          className="min-h-[28rem] w-full rounded-xl border border-border/80 bg-white shadow-[0_12px_30px_rgba(2,2,12,0.12)]"
        />
      </div>
    )
  }

  if (text) {
    return (
      <pre className="whitespace-pre-wrap rounded-xl border border-border/80 bg-card/70 p-5 font-sans text-sm leading-6">
        {text}
      </pre>
    )
  }

  if (preview) {
    return <p className="text-sm text-muted-foreground italic">{preview}</p>
  }

  return <p className="text-sm text-muted-foreground italic">No body content available.</p>
}
